import React, { useState, useEffect } from "react";
import { ConstructorEventUpdateSQLStatus } from "../../../../Event/event";


const styles = {
  body: {
    fontFamily: "'Comic Sans MS', cursive, sans-serif",
    background: "#e0f7fa",
    textAlign: "center",
    padding: 20,
    color: "#006064",
    minHeight: "100vh",
  },
  h1: {
    color: "#004d40",
  },
  scenario: {
    fontSize: "1.4rem",
    marginBottom: 20,
    background: "#b2dfdb",
    padding: 15,
    borderRadius: 10,
    maxWidth: 600,
    marginLeft: "auto",
    marginRight: "auto",
    minHeight: 60,
  },
  ul: {
    listStyleType: "none",
    padding: 0,
    maxWidth: 600,
    margin: "0 auto 20px auto",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 15,
  },
  li: {
    background: "#4db6ac",
    padding: "12px 30px",
    borderRadius: 12,
    fontSize: "1.2rem",
    color: "white",
    cursor: "pointer",
    userSelect: "none",
    minWidth: 130,
    outline: "none",
    opacity: 1, // fadeIn is automatic in React
    transition: "background-color 0.3s, transform 0.2s",
  },
  liHoverFocus: {
    backgroundColor: "#00796b",
    transform: "scale(1.05)",
    outline: "3px solid #004d40",
  },
  liSelected: {
    backgroundColor: "#c62828",
    transform: "scale(1.1)",
    animation: "bounce 0.5s",
  },
  counts: {
    fontSize: "1.2rem",
    maxWidth: 600,
    margin: "0 auto 20px auto",
    background: "#80cbc4",
    borderRadius: 10,
    padding: 15,
    minHeight: 90,
    whiteSpace: "pre-line",
  },
  feedback: {
    fontSize: "1.3rem",
    fontWeight: "bold",
    minHeight: 40,
    marginBottom: 15,
    borderRadius: 8,
    transition: "background-color 0.5s",
  },
  feedbackCorrect: {
    backgroundColor: "#a5d6a7",
    color: "#2e7d32",
  },
  feedbackWrong: {
    backgroundColor: "#ef9a9a",
    color: "#b71c1c",
  },
  button: {
    backgroundColor: "#00695c",
    color: "white",
    padding: "12px 30px",
    fontSize: "1.1rem",
    border: "none",
    borderRadius: 12,
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  buttonHover: {
    backgroundColor: "#004d40",
  },
  buttonDisabled: {
    backgroundColor: "#80cbc4",
    cursor: "not-allowed",
  },

  // Keyframes bounce animation for inline styles can't be done directly,
  // but we can add global CSS or use styled-components. For simplicity, omitted here.
};

const questions = [
  {
    category: "Fruits",
    items: [
      "Apple",
      "Banana",
      "Orange",
      "Apple",
      "Banana",
      "Apple",
      "Orange",
      "Apple",
    ],
  },
  {
    category: "Colors",
    items: [
      "Red",
      "Blue",
      "Green",
      "Blue",
      "Red",
      "Blue",
      "Yellow",
      "Blue",
      "Red",
    ],
  },
  {
    category: "Pets",
    items: ["Dog", "Cat", "Dog", "Rabbit", "Dog", "Cat", "Dog", "Fish"],
  },
  {
    category: "Ice Cream Flavors",
    items: ["Vanilla", "Chocolate", "Vanilla", "Strawberry", "Chocolate", "Vanilla"],
  },
  {
    category: "Sports",
    items: ["Soccer", "Basketball", "Soccer", "Tennis", "Soccer", "Basketball", "Tennis"],
  },
  {
    category: "Vehicles",
    items: ["Car", "Bike", "Car", "Bus", "Car", "Bike", "Bus", "Car"],
  },
  {
    category: "Animals",
    items: ["Lion", "Tiger", "Lion", "Elephant", "Tiger", "Lion", "Elephant", "Lion"],
  },
  {
    category: "Desserts",
    items: ["Cake", "Pie", "Cake", "Ice Cream", "Pie", "Cake", "Pie", "Cake"],
  },
];

function findMode(freqMap) {
  let maxCount = 0;
  let mode = null;
  for (const [item, count] of Object.entries(freqMap)) {
    if (count > maxCount) {
      maxCount = count;
      mode = item;
    }
  }
  return mode;
}

export default function ModeFinderGame() {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [freqMap, setFreqMap] = useState({});
  const [modeItem, setModeItem] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [feedbackClass, setFeedbackClass] = useState("");
  const [nextEnabled, setNextEnabled] = useState(false);

  useEffect(() => {
    nextQuestion();
  }, []);

  function nextQuestion() {
    setNextEnabled(false);
    setSelectedItem(null);
    setFeedback("");
    setFeedbackClass("");
    const question = questions[Math.floor(Math.random() * questions.length)];
    setCurrentQuestion(question);

    // build freqMap
    const map = {};
    question.items.forEach((item) => {
      map[item] = (map[item] || 0) + 1;
    });
    setFreqMap(map);

    // find mode
    const mode = findMode(map);
    setModeItem(mode);
  }

  function selectItem(item) {
    setSelectedItem(item);
    if (item === modeItem) {
      setFeedback("🎉 Great! You picked the most popular item!");
      setFeedbackClass("correct");
      setNextEnabled(true);
      const responsedata = ConstructorEventUpdateSQLStatus();
      console.log("Event completed", responsedata);
    } else {
      setFeedback("Oops! Try again or pick another.");
      setFeedbackClass("wrong");
      setNextEnabled(false);
    }
  }

  // Unique items for rendering buttons
  const uniqueItems = currentQuestion
    ? [...new Set(currentQuestion.items)]
    : [];

  return (
    <div style={styles.body}>
      <h3 style={styles.h1}>Most Popular Item - Find the Mode!</h3>

      <div
        id="scenario"
        aria-live="polite"
        style={styles.scenario}
      >
        {currentQuestion
          ? `Which is the most popular ${currentQuestion.category}? Click your choice below.`
          : "Loading items..."}
      </div>

      <ul
        id="itemList"
        aria-label="List of items to select from"
        style={styles.ul}
      >
        {uniqueItems.map((item, i) => {
          const isSelected = item === selectedItem;
          return (
            <li
              key={item}
              tabIndex={0}
              role="button"
              aria-pressed={isSelected}
              onClick={() => selectItem(item)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  selectItem(item);
                }
              }}
              style={{
                ...styles.li,
                ...(isSelected ? styles.liSelected : {}),
                cursor: "pointer",
              }}
            >
              {item}
            </li>
          );
        })}
      </ul>

      <div
        className="counts"
        aria-live="polite"
        id="countsDisplay"
        style={styles.counts}
      >
        <strong>Frequencies:</strong>
        <br />
        {Object.entries(freqMap).map(([item, count]) => (
          <div key={item}>
            {item}: {count}
          </div>
        ))}
      </div>

      <div
        id="feedback"
        aria-live="assertive"
        style={{
          ...styles.feedback,
          ...(feedbackClass === "correct"
            ? styles.feedbackCorrect
            : feedbackClass === "wrong"
              ? styles.feedbackWrong
              : {}),
        }}
      >
        {feedback}
      </div>

      <button
        id="nextBtn"
        onClick={nextQuestion}
        disabled={!nextEnabled}
        style={{
          ...styles.button,
          ...(nextEnabled ? {} : styles.buttonDisabled),
        }}
      >
        Next Question
      </button>
    </div>
  );
}
